import * as React from "react";
import { ISaveData } from "../../../store/client/clientItem";
import { Card, CardContent, CardHeader, Grid, Typography, Box } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { ClientIcon } from "../../atoms/icons/ClientIcon";
import { TextInput } from "../../molecules/forms/TextInput";
import { DateInput } from "../../molecules/forms/DateInput";
import { GenderSelect } from "../../molecules/forms/GenderSelect";
import { RijksregisterNummerInput } from "../../molecules/forms/RijksregisterNummerInput";
import { DisabilityStatusSelect } from "../../molecules/forms/DisabilityStatusSelect";
import { CitySelect } from "../../molecules/forms/CitySelect";
import { LanguageSelect } from "../../molecules/forms/LanguageSelect";
import { ActivityTypeSelect } from "../../molecules/forms/ActivityTypeSelect";
import { DisabilityMultiSelect } from "../../molecules/forms/DisabilityMultiSelect";
import { ProblemMultiSelect } from "../../molecules/forms/ProblemMultiSelect";
import { ContactPersonsList, IContactPersonData } from "./ContactPersonsList";
import { useTranslation } from 'react-i18next';

// All the incoming values
interface IProps {
    streamId?: string;
    initialData?: Partial<ISaveData>;
    loading?: boolean;
    contactPersonTypes?: Array<{ streamId: string; name: string }>;
    /** When true (editing existing client), enables dirty tracking and contact person save reminder */
    isEditMode?: boolean;
}

// Outgoing functions
interface IDispatch {
    onChange: (data: ISaveData, isValid: boolean) => void;
    /** Called whenever the user makes a change to the form (edit mode only) */
    onDirtyChange?: () => void;
}

// Actual Component
export type AllProps = IProps & IDispatch;
export const ClientEdit = (props: AllProps) => {
    const { t: tClient } = useTranslation('client');

    // Form state - initialize only once with initialData
    const [formData, setFormData] = React.useState<ISaveData>(() => ({
        firstName: props.initialData?.firstName || '',
        lastName: props.initialData?.lastName || '',
        burgerServiceNumber: props.initialData?.burgerServiceNumber || '',
        dateOfBirth: props.initialData?.dateOfBirth || null,
        gender: props.initialData?.gender || null,
        officialStreet: props.initialData?.officialStreet || '',
        residencyStreet: props.initialData?.residencyStreet || null,
        officialStreetNumber: props.initialData?.officialStreetNumber || '',
        residencyStreetNumber: props.initialData?.residencyStreetNumber || null,
        homePhone: props.initialData?.homePhone || null,
        workPhone: props.initialData?.workPhone || null,
        mobilePhone: props.initialData?.mobilePhone || null,
        email: props.initialData?.email || null,
        contactPerson: props.initialData?.contactPerson || null,
        contactPersons: props.initialData?.contactPersons || [],
        remarks: props.initialData?.remarks || null,
        disabilityStatus: props.initialData?.disabilityStatus || 'None',
        languageStreamId: props.initialData?.languageStreamId || null,
        residencyCityStreamId: props.initialData?.residencyCityStreamId || null,
        officialCityStreamId: props.initialData?.officialCityStreamId || '',
        activityTypeStreamId: props.initialData?.activityTypeStreamId || null,
        disabilityId: props.initialData?.disabilityId || [],
        problemId: props.initialData?.problemId || []
    }));

    // Track whether gender and dateOfBirth were auto-populated from rijksregisternummer
    // If initial data has a burgerServiceNumber with gender/dateOfBirth, disable those fields
    const [isGenderAutoPopulated, setIsGenderAutoPopulated] = React.useState<boolean>(
        !!(props.initialData?.burgerServiceNumber && props.initialData?.gender)
    );
    const [isDateOfBirthAutoPopulated, setIsDateOfBirthAutoPopulated] = React.useState<boolean>(
        !!(props.initialData?.burgerServiceNumber && props.initialData?.dateOfBirth)
    );

    // Track the current streamId to detect when new data is loaded
    const prevStreamIdRef = React.useRef(props.streamId);

    // Update form data when new data is loaded (streamId changes or loading completes)
    React.useEffect(() => {
        // Only update if streamId has changed or if we're transitioning from loading to loaded
        if (props.streamId && props.streamId !== prevStreamIdRef.current && !props.loading && props.initialData) {
            prevStreamIdRef.current = props.streamId;
            
            // If initial data has burgerServiceNumber with gender/dateOfBirth, disable those fields
            setIsGenderAutoPopulated(!!(props.initialData?.burgerServiceNumber && props.initialData?.gender));
            setIsDateOfBirthAutoPopulated(!!(props.initialData?.burgerServiceNumber && props.initialData?.dateOfBirth));
            
            setFormData({
                firstName: props.initialData?.firstName || '',
                lastName: props.initialData?.lastName || '',
                burgerServiceNumber: props.initialData?.burgerServiceNumber || '',
                dateOfBirth: props.initialData?.dateOfBirth || null,
                gender: props.initialData?.gender || null,
                officialStreet: props.initialData?.officialStreet || '',
                residencyStreet: props.initialData?.residencyStreet || null,
                officialStreetNumber: props.initialData?.officialStreetNumber || '',
                residencyStreetNumber: props.initialData?.residencyStreetNumber || null,
                homePhone: props.initialData?.homePhone || null,
                workPhone: props.initialData?.workPhone || null,
                mobilePhone: props.initialData?.mobilePhone || null,
                email: props.initialData?.email || null,
                contactPerson: props.initialData?.contactPerson || null,
                contactPersons: props.initialData?.contactPersons || [],
                remarks: props.initialData?.remarks || null,
                disabilityStatus: props.initialData?.disabilityStatus || 'None',
                languageStreamId: props.initialData?.languageStreamId || null,
                residencyCityStreamId: props.initialData?.residencyCityStreamId || null,
                officialCityStreamId: props.initialData?.officialCityStreamId || '',
                activityTypeStreamId: props.initialData?.activityTypeStreamId || null,
                disabilityId: props.initialData?.disabilityId || [],
                problemId: props.initialData?.problemId || []
            });
        }
    }, [props.streamId, props.loading]);

    // Validation state
    const [errors, setErrors] = React.useState<{[key: string]: [boolean, string]}>({});
    const [formValid, setFormValid] = React.useState<boolean>(false);

    // Validate form
    const validateForm = React.useCallback((data: ISaveData): boolean => {
        // Required fields validation
        const hasFirstName = !!data.firstName && data.firstName.trim().length > 0;
        const hasLastName = !!data.lastName && data.lastName.trim().length > 0;
        const hasBurgerServiceNumber = !!data.burgerServiceNumber && data.burgerServiceNumber.trim().length > 0;
        const hasOfficialStreet = !!data.officialStreet && data.officialStreet.trim().length > 0;
        const hasOfficialStreetNumber = !!data.officialStreetNumber && data.officialStreetNumber.trim().length > 0;
        const hasOfficialCity = !!data.officialCityStreamId && data.officialCityStreamId.trim().length > 0;
        const hasDisabilityStatus = !!data.disabilityStatus;

        // Check if there are any field-level errors
        const hasFieldErrors = Object.keys(errors).length > 0;

        return hasFirstName && hasLastName && hasBurgerServiceNumber && 
               hasOfficialStreet && hasOfficialStreetNumber && hasOfficialCity &&
               hasDisabilityStatus && !hasFieldErrors;
    }, [errors]);

    // Update form validity whenever formData or errors change
    React.useEffect(() => {
        const isValid = validateForm(formData);
        setFormValid(isValid);
    }, [formData, errors, validateForm]);

    // Validate legacy contactPerson field - should be empty
    React.useEffect(() => {
        if (formData.contactPerson && formData.contactPerson.trim().length > 0) {
            setErrors(prev => ({
                ...prev,
                contactPerson: [true, tClient('contactPersonLegacyClearRequired')]
            }));
        } else {
            setErrors(prev => {
                const { contactPerson, ...rest } = prev;
                return rest;
            });
        }
    }, [formData.contactPerson]);

    // Notify parent of changes - use useRef to track if this is the initial render
    const isInitialMount = React.useRef(true);
    React.useEffect(() => {
        // Skip the first render to avoid calling onChange with initial empty values
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        props.onChange(formData, formValid);
    }, [formData, formValid]);

    // Update form field
    const updateField = <K extends keyof ISaveData>(field: K, value: ISaveData[K], isValid: boolean = true) => {
        props.onDirtyChange?.();
        setFormData(prev => ({ ...prev, [field]: value }));
        if (!isValid) {
            setErrors(prev => ({ ...prev, [field]: [true, 'Invalid value'] }));
        } else {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };


    return (
        <Card>
            <CardHeader 
                title={formData.firstName && formData.lastName 
                    ? `${formData.firstName} ${formData.lastName}` 
                    : tClient('editClient')} 
                subheader={props.streamId ? `Client nummer: ${props.streamId}` : ''} 
                avatar={<ClientIcon />} 
            /> 
            <CardContent>
                <Grid container spacing={3}>

                    {/* Blok 1: Cliëntgegevens */}
                    <Grid item xs={12} md={6} lg={4}>
                        <Typography gutterBottom variant="h5" component="h2">
                            {tClient('clientDataTitle')}
                        </Typography>

                        <TextInput
                            label={tClient('firstNameLabel')}
                            value={formData.firstName}
                            required={true}
                            error={errors.firstName}
                            loading={props.loading}
                            onChange={(value, isValid) => updateField('firstName', value || '', isValid)}
                        />

                        <TextInput
                            label={tClient('lastNameLabel')}
                            value={formData.lastName}
                            required={true}
                            error={errors.lastName}
                            loading={props.loading}
                            onChange={(value, isValid) => updateField('lastName', value || '', isValid)}
                        />

                        <RijksregisterNummerInput
                            label={tClient('burgerServiceNumberLabel')}
                            value={formData.burgerServiceNumber}
                            required={true}
                            error={errors.burgerServiceNumber}
                            loading={props.loading}
                            onChangeWithData={(value, isValid, data) => {
                                // Update burgerServiceNumber
                                updateField('burgerServiceNumber', value || '', isValid);
                                
                                // Auto-populate gender and birth date from valid INSZ
                                if (data && isValid) {
                                    setFormData(prev => {
                                        const updates: Partial<ISaveData> = {};
                                        
                                        // Update gender if extracted from INSZ
                                        if (data.sex === 'Male') {
                                            updates.gender = 'Male';
                                            setIsGenderAutoPopulated(true);
                                        } else if (data.sex === 'Female') {
                                            updates.gender = 'Female';
                                            setIsGenderAutoPopulated(true);
                                        }
                                        
                                        // Update birth date if extracted from INSZ
                                        if (data.birthDate) {
                                            updates.dateOfBirth = data.birthDate.toISOString();
                                            setIsDateOfBirthAutoPopulated(true);
                                        }
                                        
                                        return { ...prev, ...updates };
                                    });
                                } else {
                                    // Reset auto-population flags if INSZ becomes invalid
                                    setIsGenderAutoPopulated(false);
                                    setIsDateOfBirthAutoPopulated(false);
                                }
                            }}
                        />

                        <DateInput
                            key={`dateOfBirth-${formData.dateOfBirth}`}
                            label={tClient('birthDateLabel')}
                            value={formData.dateOfBirth ? new Date(formData.dateOfBirth) : null}
                            maxValue={new Date()}
                            loading={props.loading}
                            disabled={isDateOfBirthAutoPopulated}
                            onChange={(value) => updateField('dateOfBirth', value ? value.toISOString() : null)}
                        />

                        <GenderSelect
                            key={`gender-${formData.gender}`}
                            label={tClient('genderLabel')}
                            value={formData.gender}
                            loading={props.loading}
                            disabled={isGenderAutoPopulated}
                            onChange={(value) => updateField('gender', value)}
                        />

                        <LanguageSelect
                            label={tClient('languageLabel')}
                            selectedLanguageId={formData.languageStreamId || undefined}
                            loading={props.loading}
                            onChange={(value) => updateField('languageStreamId', value?.id || null)}
                        />

                        <TextInput
                            label={tClient('remarksLabel')}
                            value={formData.remarks || ''}
                            multiline={true}
                            rows={4}
                            loading={props.loading}
                            onChange={(value) => updateField('remarks', value)}
                        />
                    </Grid>

                    {/* Blok 2: Contactgegevens */}
                    <Grid item xs={12} md={6} lg={4}>
                        <Typography gutterBottom variant="h5" component="h2">
                            {tClient('contactDataTitle')}
                        </Typography>

                        <TextInput
                            label={tClient('officialStreetLabel')}
                            value={formData.officialStreet}
                            required={true}
                            error={errors.officialStreet}
                            loading={props.loading}
                            onChange={(value, isValid) => updateField('officialStreet', value || '', isValid)}
                        />

                        <TextInput
                            label={tClient('officialStreetNumberLabel')}
                            value={formData.officialStreetNumber}
                            required={true}
                            error={errors.officialStreetNumber}
                            loading={props.loading}
                            onChange={(value, isValid) => updateField('officialStreetNumber', value || '', isValid)}
                        />

                        <CitySelect
                            label={tClient('officialCityLabel')}
                            selectedCityId={formData.officialCityStreamId}
                            required={true}
                            error={errors.officialCityStreamId}
                            loading={props.loading}
                            onChange={(value) => updateField('officialCityStreamId', value?.id || '')}
                        />

                        <TextInput
                            label={tClient('residencyStreetLabel')}
                            value={formData.residencyStreet || ''}
                            loading={props.loading}
                            onChange={(value) => updateField('residencyStreet', value || null)}
                        />

                        <TextInput
                            label={tClient('residencyStreetNumberLabel')}
                            value={formData.residencyStreetNumber || ''}
                            loading={props.loading}
                            onChange={(value) => updateField('residencyStreetNumber', value || null)}
                        />

                        <CitySelect
                            label={tClient('residencyCityLabel')}
                            selectedCityId={formData.residencyCityStreamId || undefined}
                            loading={props.loading}
                            onChange={(value) => updateField('residencyCityStreamId', value?.id || null)}
                        />

                        <TextInput
                            label={tClient('homePhoneLabel')}
                            value={formData.homePhone || ''}
                            loading={props.loading}
                            onChange={(value) => updateField('homePhone', value || null)}
                        />

                        <TextInput
                            label={tClient('otherPhoneLabel')}
                            value={formData.workPhone || ''}
                            loading={props.loading}
                            onChange={(value) => updateField('workPhone', value || null)}
                        />

                        <TextInput
                            label={tClient('mobilePhoneLabel')}
                            value={formData.mobilePhone || ''}
                            loading={props.loading}
                            onChange={(value) => updateField('mobilePhone', value || null)}
                        />

                        <TextInput
                            label={tClient('emailLabel')}
                            value={formData.email || ''}
                            loading={props.loading}
                            onChange={(value) => updateField('email', value || null)}
                        />

                        <ContactPersonsList
                            contactPersons={formData.contactPersons}
                            contactPersonTypes={props.contactPersonTypes || []}
                            loading={props.loading}
                            isEditMode={props.isEditMode || false}
                            onChange={(value, isValid) => {
                                props.onDirtyChange?.();
                                if (!isValid) {
                                    setErrors(prev => ({ ...prev, contactPersons: [true, 'Invalid'] }));
                                } else {
                                    setErrors(prev => {
                                        const { contactPersons, ...rest } = prev;
                                        return rest;
                                    });
                                }
                                setFormData(prev => ({ ...prev, contactPersons: value }));
                            }}
                        />

                        {/* Legacy field - only show when it has content */}
                        {formData.contactPerson !== null && formData.contactPerson !== "" && (
                            <Box mt={2}>
                                <Alert severity="warning" variant="outlined">
                                    {tClient('contactPersonLegacyWarning')}
                                </Alert>
                                <TextInput
                                    label={tClient('contactPersonLabel')}
                                    value={formData.contactPerson || ''}
                                    loading={props.loading}
                                    error={errors.contactPerson}
                                    onChange={(value) => updateField('contactPerson', value || null)}
                                />
                            </Box>
                        )}
                    </Grid>

                    {/* Blok 3: Beperkingen en bijkomende uitdagingen */}
                    <Grid item xs={12} md={6} lg={4}>
                        <Typography gutterBottom variant="h5" component="h2">
                            {tClient('disabilityDataTitle')}
                        </Typography>

                        <ActivityTypeSelect
                            label={tClient('activityLabel')}
                            selectedActivityTypeId={formData.activityTypeStreamId || undefined}
                            loading={props.loading}
                            onChange={(value) => updateField('activityTypeStreamId', value?.id || null)}
                        />

                        <DisabilityStatusSelect
                            label={tClient('disabilityStatusLabel')}
                            value={formData.disabilityStatus}
                            required={true}
                            loading={props.loading}
                            onChange={(value) => updateField('disabilityStatus', value || 'None')}
                        />

                        <DisabilityMultiSelect
                            label={tClient('disabilityTypesLabel')}
                            selectedDisabilityIds={formData.disabilityId}
                            loading={props.loading}
                            onChange={(value) => updateField('disabilityId', value?.map(d => d.id) || [])}
                        />

                        <ProblemMultiSelect
                            label={tClient('problemsLabel')}
                            selectedProblemIds={formData.problemId}
                            loading={props.loading}
                            onChange={(value) => updateField('problemId', value?.map(p => p.id) || [])}
                        />
                    </Grid>

                </Grid>
            </CardContent>
        </Card>
    );
}

